<app-ssc-shell title="Dashboard">
  <section class="dashboard-page">
    <div class="dashboard-hero">
      <div>
        <span class="dashboard-eyebrow">Translation Operations</span>
        <h2>SSC AI Translation Analytics</h2>
        <p>Live view of user-provided questions, translated outputs, review state, quality scores, and language asset readiness.</p>
      </div>
      <button class="ssc-btn" type="button" (click)="loadAnalytics()" [disabled]="loading">
        {{ loading ? 'Refreshing...' : 'Refresh' }}
      </button>
    </div>

    @if (errorMessage) {
      <div class="dashboard-error">{{ errorMessage }}</div>
    }

    <div class="dashboard-kpis">
      <div class="ssc-card dashboard-kpi">
        <span>Input Questions</span>
        <strong>{{ summary.total_questions }}</strong>
        <small>{{ summary.today_questions }} added today</small>
      </div>
      <div class="ssc-card dashboard-kpi">
        <span>Translations</span>
        <strong>{{ summary.total_translations }}</strong>
        <small>{{ summary.target_languages }} target languages</small>
      </div>
      <div class="ssc-card dashboard-kpi">
        <span>Avg Confidence</span>
        <strong>{{ formatPercent(quality.avg_confidence) }}</strong>
        <small>{{ quality.high_confidence }} high confidence outputs</small>
      </div>
      <div class="ssc-card dashboard-kpi">
        <span>Bulk Files</span>
        <strong>{{ summary.total_files }}</strong>
        <small>{{ summary.total_images }} image inputs captured</small>
      </div>
    </div>

    <div class="dashboard-grid">
      <div class="ssc-card dashboard-panel trend-panel">
        <div class="panel-title">
          <h3>7-Day Translation Activity</h3>
          <span>Questions vs translations</span>
        </div>
        @if (analytics.daily_trend.length) {
          <div class="trend-chart">
            @for (point of analytics.daily_trend; track point.date) {
              <div class="trend-day">
                <div class="trend-bars">
                  <span class="bar questions" [style.height.%]="barHeight(point.questions)" [title]="'Questions: ' + point.questions"></span>
                  <span class="bar translations" [style.height.%]="barHeight(point.translations)" [title]="'Translations: ' + point.translations"></span>
                </div>
                <small>{{ point.date }}</small>
              </div>
            }
          </div>
          <div class="chart-legend"><span class="dot questions"></span> Questions <span class="dot translations"></span> Translations</div>
        } @else {
          <div class="empty-state">No translation activity has been captured yet.</div>
        }
      </div>

      <div class="ssc-card dashboard-panel">
        <div class="panel-title">
          <h3>Quality Snapshot</h3>
          <span>Latest stored scores</span>
        </div>
        <div class="quality-list">
          <div class="quality-row">
            <span>Confidence</span>
            <b>{{ quality.avg_confidence || 0 }}%</b>
            <div class="metric-bar"><i [style.width.%]="metricWidth(quality.avg_confidence)"></i></div>
          </div>
          <div class="quality-row">
            <span>Adequacy</span>
            <b>{{ quality.avg_adequacy || 0 }} / 5</b>
            <div class="metric-bar"><i [style.width.%]="metricWidth(quality.avg_adequacy, 5)"></i></div>
          </div>
          <div class="quality-row">
            <span>Fluency</span>
            <b>{{ quality.avg_fluency || 0 }} / 5</b>
            <div class="metric-bar"><i [style.width.%]="metricWidth(quality.avg_fluency, 5)"></i></div>
          </div>
        </div>
        <div class="quality-flags">
          <span class="ssc-badge green">{{ quality.high_confidence }} High Confidence</span>
          <span class="ssc-badge amber">{{ quality.low_confidence }} Low Confidence</span>
        </div>
      </div>
    </div>

    <div class="dashboard-grid three">
      <div class="ssc-card dashboard-panel">
        <div class="panel-title">
          <h3>Review Status</h3>
          <span>Translation lifecycle</span>
        </div>
        @for (item of analytics.status_breakdown; track item.label) {
          <div class="breakdown-row">
            <div><span>{{ item.label }}</span><b>{{ item.count }}</b></div>
            <div class="breakdown-bar"><i [style.width.%]="breakdownPercent(item, analytics.status_breakdown)"></i></div>
          </div>
        } @empty {
          <div class="empty-state">No review statuses yet.</div>
        }
      </div>

      <div class="ssc-card dashboard-panel">
        <div class="panel-title">
          <h3>Input Sources</h3>
          <span>Single, bulk, and images</span>
        </div>
        @for (item of analytics.upload_type_breakdown; track item.label) {
          <div class="breakdown-row">
            <div><span>{{ item.label }}</span><b>{{ item.count }}</b></div>
            <div class="breakdown-bar neutral"><i [style.width.%]="breakdownPercent(item, analytics.upload_type_breakdown)"></i></div>
          </div>
        } @empty {
          <div class="empty-state">No input source data yet.</div>
        }
      </div>

      <div class="ssc-card dashboard-panel">
        <div class="panel-title">
          <h3>Language Assets</h3>
          <span>Reusable knowledge base</span>
        </div>
        <div class="asset-mini-grid">
          <div><span>Glossary</span><b>{{ analytics.asset_summary.glossary_terms }}</b></div>
          <div><span>TM</span><b>{{ analytics.asset_summary.tm_segments }}</b></div>
          <div><span>Corpus</span><b>{{ analytics.asset_summary.corpus_entries }}</b></div>
          <div><span>Benchmark</span><b>{{ analytics.asset_summary.benchmark_items }}</b></div>
        </div>
      </div>
    </div>

    <div class="dashboard-grid">
      <div class="ssc-card dashboard-panel table-panel">
        <div class="panel-title">
          <h3>Top Language Pairs</h3>
          <span>By translated outputs</span>
        </div>
        <table class="ssc-table dashboard-table">
          <thead><tr><th>Pair</th><th>Translations</th><th>Confidence</th></tr></thead>
          <tbody>
            @for (row of analytics.language_pairs; track row.source_language + row.target_language) {
              <tr>
                <td>{{ row.source_language }} -> {{ row.target_language }}</td>
                <td>{{ row.translations }}</td>
                <td><span class="confidence" [class]="confidenceClass(row.avg_confidence)">{{ formatPercent(row.avg_confidence) }}</span></td>
              </tr>
            } @empty {
              <tr><td colspan="3">No language pair data yet.</td></tr>
            }
          </tbody>
        </table>
      </div>

      <div class="ssc-card dashboard-panel table-panel">
        <div class="panel-title">
          <h3>Subject Coverage</h3>
          <span>Questions by subject</span>
        </div>
        <table class="ssc-table dashboard-table">
          <thead><tr><th>Subject</th><th>Questions</th><th>Confidence</th></tr></thead>
          <tbody>
            @for (row of analytics.subjects; track row.subject) {
              <tr>
                <td>{{ row.subject }}</td>
                <td>{{ row.questions }}</td>
                <td><span class="confidence" [class]="confidenceClass(row.avg_confidence)">{{ formatPercent(row.avg_confidence) }}</span></td>
              </tr>
            } @empty {
              <tr><td colspan="3">No subject data yet.</td></tr>
            }
          </tbody>
        </table>
      </div>
    </div>

    <div class="ssc-card dashboard-panel recent-panel">
      <div class="panel-title">
        <h3>Recent User Inputs</h3>
        <span>Latest stored question history</span>
      </div>
      <table class="ssc-table dashboard-table">
        <thead><tr><th>Question</th><th>Type</th><th>Subject</th><th>Translations</th><th>Status</th><th>Created</th></tr></thead>
        <tbody>
          @for (row of analytics.recent_questions; track row.id) {
            <tr>
              <td>{{ row.question }}</td>
              <td>{{ row.upload_type }}</td>
              <td>{{ row.subject }}</td>
              <td>{{ row.translations }}</td>
              <td>{{ row.status }}</td>
              <td>{{ row.created_at }}</td>
            </tr>
          } @empty {
            <tr><td colspan="6">No questions have been translated yet.</td></tr>
          }
        </tbody>
      </table>
    </div>
  </section>
</app-ssc-shell>
